import { api } from './api';

export interface LessonProgress {
  id: string;
  lessonId: string;
  userId: string;
  completed: boolean;
  watchedSeconds: number;
  totalSeconds?: number;
  lastWatchedAt?: string;
  completedAt?: string;
  notes?: string | null;
}

export interface ContinueLesson {
  lessonId: string;
  title: string;
  lastPosition: number;
  progress: number;
}

export interface CourseProgress {
  courseId: string;
  progress: number;
  totalLessons: number;
  completedLessons: number;
  currentLesson?: {
    id: string;
    title: string;
    moduleTitle: string;
  };
  lastAccessedAt?: string;
}

export interface WatchHistoryEntry {
  id: string;
  lessonId: string;
  watchedAt: string;
  duration: number;
  lesson: {
    id: string;
    title: string;
    course: {
      id: string;
      title: string;
      slug: string;
    };
  };
}

class ProgressService {
  async getCourseProgress(courseId: string): Promise<CourseProgress> {
    const { data } = await api.get(`/progress/courses/${courseId}`);
    return data.data || data;
  }

  async getLessonProgress(lessonId: string): Promise<LessonProgress | null> {
    try {
      const { data } = await api.get(`/progress/lessons/${lessonId}`);
      return data.data || data;
    } catch {
      return null;
    }
  }

  async updateLessonProgress(
    lessonId: string,
    progress: { watchedSeconds: number; totalSeconds?: number },
  ): Promise<LessonProgress> {
    const { data } = await api.put(`/progress/lessons/${lessonId}`, {
      lastPosition: Math.floor(progress.watchedSeconds),
    });
    return data.data || data;
  }

  async saveLessonNotes(
    lessonId: string,
    notes: string,
  ): Promise<LessonProgress> {
    const { data } = await api.put(`/progress/lessons/${lessonId}/notes`, {
      notes,
    });
    return data.data || data;
  }

  async markLessonComplete(lessonId: string): Promise<LessonProgress> {
    const { data } = await api.post(`/progress/lessons/${lessonId}/complete`);
    return data.data || data;
  }

  async markLessonIncomplete(lessonId: string): Promise<LessonProgress> {
    // Backend uses DELETE to unmark complete, or just update with completed: false
    const { data } = await api.put(`/progress/lessons/${lessonId}`, {
      completed: false,
    });
    return data.data || data;
  }

  async getContinueLesson(): Promise<ContinueLesson | null> {
    try {
      const { data } = await api.get('/progress/continue');
      const payload = data && 'success' in data ? data.data : data;
      return payload?.lessonId ? payload : null;
    } catch {
      return null;
    }
  }

  async getWatchHistory(limit: number = 10): Promise<WatchHistoryEntry[]> {
    try {
      const { data } = await api.get('/progress/watch-history', {
        params: { limit },
      });
      return data.data || data;
    } catch (error) {
      console.warn('Failed to fetch watch history:', error);
      return [];
    }
  }

  async getNextLesson(courseId: string): Promise<{
    lessonId: string;
    lesson: any;
  } | null> {
    try {
      const { data } = await api.get(
        `/progress/courses/${courseId}/next-lesson`,
      );
      return data.data || data;
    } catch {
      return null;
    }
  }

  async recordWatchTime(lessonId: string, duration: number): Promise<void> {
    try {
      await api.post(`/progress/lessons/${lessonId}/watch-time`, {
        duration,
      });
    } catch (error) {
      console.warn('Failed to record watch time:', error);
    }
  }
}

export const progressService = new ProgressService();
